import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, LogIn, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

function PageHeaderLite({ title }) {
  return <h1 className="text-2xl font-heading font-bold text-[#0B1B3D] mb-6">{title}</h1>;
}

export default function EmployeeWorkspace() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [leave, setLeave] = useState({ type: "casual", from_date: "", to_date: "", reason: "" });
  const [onboarding, setOnboarding] = useState(null);
  const [claims, setClaims] = useState([]);
  const [docs, setDocs] = useState([]);
  const [goals, setGoals] = useState([]);
  const [claim, setClaim] = useState({ category: "travel", amount: "", date: "", description: "" });
  const [itemFor, setItemFor] = useState(null);
  const [itemData, setItemData] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const [assessFor, setAssessFor] = useState(null);
  const [assess, setAssess] = useState({ rating: 4, notes: "" });
  const [assets, setAssets] = useState([]);
  const [calendar, setCalendar] = useState({ holidays: [], shift: null });
  const [exitInfo, setExitInfo] = useState(null);
  const [resignModal, setResignModal] = useState(false);
  const [resign, setResign] = useState({ reason: "", proposed_last_date: "" });
  const [signFor, setSignFor] = useState(null);
  const [signName, setSignName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/employee/me").then(({ data }) => setMe(data)).catch(() => setMe({ employee: null }));
    api.get("/hrms/attendance/mine").then(({ data }) => setAttendance(data.attendance)).catch(() => {});
    api.get("/hrms/leaves/mine").then(({ data }) => setLeaves(data.leaves)).catch(() => {});
    api.get("/hrms/payslips/mine").then(({ data }) => setPayslips(data.payslips)).catch(() => {});
    api.get("/onboarding/me").then(({ data }) => setOnboarding(data.onboarding)).catch(() => {});
    api.get("/hrms/reimbursements/mine").then(({ data }) => setClaims(data.claims)).catch(() => {});
    api.get("/documents/mine").then(({ data }) => setDocs(data.documents)).catch(() => {});
    api.get("/performance/me").then(({ data }) => setGoals(data.goals)).catch(() => {});
    api.get("/hrms/assets/mine").then(({ data }) => setAssets(data.assets)).catch(() => {});
    api.get("/hrms/calendar").then(({ data }) => setCalendar(data)).catch(() => {});
    api.get("/hrms/exit/me").then(({ data }) => setExitInfo(data.exit)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  if (!me) return <p className="text-muted-foreground">Loading…</p>;

  if (!me.employee) {
    const hasLegacy = (docs || []).length > 0 || (exitInfo && exitInfo.status === "exited");
    if (!hasLegacy) {
      return (
        <EmptyState
          title="No active employment record"
          description="When you join a company through Job Spring, your employee workspace appears here automatically."
          cta="Find Jobs"
          onCta={() => navigate("/jobs")}
          testId="employee-empty"
        />
      );
    }
    return (
      <div data-testid="employee-workspace-exited">
        <PageHeaderLite title="My Documents & Exit Record" />
        {exitInfo && (
          <div className="bg-white border border-border/60 rounded-xl p-6 mb-6" data-testid="exit-status-card">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading font-semibold text-[#0B1B3D]">Exit completed</p>
              <StatusBadge status={exitInfo.status} />
            </div>
            {exitInfo.fnf_amount > 0 && (
              <p className="text-sm">Full & final settlement: <span className="font-bold">{inr(exitInfo.fnf_amount)}</span></p>
            )}
          </div>
        )}
        <div className="space-y-3" data-testid="my-docs-list">
          {docs.map((d) => (
            <div key={d.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between" data-testid={`my-doc-${d.id}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#2563EB]/10"><FileText className="w-4 h-4 text-[#2563EB]" /></div>
                <div>
                  <p className="font-semibold text-sm">{d.template_name}</p>
                  <p className="text-xs text-muted-foreground">{d.company_name} · {fmtDate(d.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.signed_at && <StatusBadge status="signed" />}
                <Button size="sm" variant="outline" className="rounded-full" onClick={async () => {
                  try {
                    const { data } = await api.get(`/documents/${d.id}`);
                    setViewDoc(data);
                  } catch (e) { toast.error(errMsg(e)); }
                }} data-testid={`view-my-doc-${d.id}`}>View</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadPdf(d)} data-testid={`pdf-my-doc-${d.id}`}>PDF</Button>
              </div>
            </div>
          ))}
        </div>
        <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
          <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="my-doc-dialog">
            <DialogHeader><DialogTitle>{viewDoc?.template_name}</DialogTitle></DialogHeader>
            <div className="bg-white border border-border rounded-lg p-6 whitespace-pre-line text-sm leading-relaxed font-serif max-h-[60vh] overflow-y-auto">
              {viewDoc?.body}
            </div>
            <Button variant="outline" className="rounded-full" onClick={() => downloadPdf(viewDoc)} data-testid="my-doc-pdf">Download PDF</Button>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const emp = me.employee;
  const today = me.today_attendance;

  const doCheck = async (kind) => {
    try {
      await api.post(`/hrms/attendance/${kind}`);
      toast.success(kind === "check-in" ? "Checked in" : "Checked out");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const applyLeave = async () => {
    setBusy(true);
    try {
      await api.post("/hrms/leaves", leave);
      toast.success("Leave request submitted for approval");
      setLeave({ type: "casual", from_date: "", to_date: "", reason: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submitClaim = async () => {
    setBusy(true);
    try {
      await api.post("/hrms/reimbursements", { ...claim, amount: Number(claim.amount) });
      toast.success("Claim submitted for approval");
      setClaim({ category: "travel", amount: "", date: "", description: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const completeItem = async () => {
    try {
      await api.post(`/onboarding/items/${itemFor.key}/complete`, { data: itemData });
      toast.success(`${itemFor.label} completed`);
      setItemFor(null);
      setItemData("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitAssess = async () => {
    try {
      await api.post(`/performance/goals/${assessFor.id}/self-assess`, { ...assess, rating: Number(assess.rating) });
      toast.success("Self-assessment submitted");
      setAssessFor(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const ackAsset = async (id) => {
    try {
      await api.post(`/hrms/assets/${id}/ack`);
      toast.success("Asset acknowledged");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitResign = async () => {
    setBusy(true);
    try {
      await api.post("/hrms/exit/resign", resign);
      toast.success("Resignation submitted to HR");
      setResignModal(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const signDoc = async () => {
    try {
      await api.post(`/documents/${signFor.id}/sign`, { name: signName });
      toast.success("Document signed");
      setSignFor(null);
      setViewDoc(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadPdf = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.template_name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="employee-workspace">
      <div className="navy-bg rounded-xl p-7 text-white mb-8" data-testid="employee-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-2xl">{emp.name}</p>
            <p className="text-white/60 text-sm mt-1">{[emp.designation, emp.department, emp.company_name].filter(Boolean).join(" · ")}</p>
            <p className="text-white/40 text-xs mt-1">{emp.emp_code} · Joined {emp.joining_date}</p>
          </div>
          <div className="flex gap-3">
            {!today?.check_in ? (
              <Button onClick={() => doCheck("check-in")} className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-semibold" data-testid="check-in-btn">
                <LogIn className="w-4 h-4 mr-1.5" /> Check In
              </Button>
            ) : !today?.check_out ? (
              <Button onClick={() => doCheck("check-out")} variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="check-out-btn">
                <LogOut className="w-4 h-4 mr-1.5" /> Check Out
              </Button>
            ) : (
              <StatusBadge status="present" />
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="attendance">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="attendance" data-testid="tab-my-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-my-leave">Leave</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="tab-my-payslips">Payslips</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-my-onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-my-claims">Reimbursements</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-my-documents">Documents</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-my-performance">Performance</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-my-assets">Assets</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-my-calendar">Holidays</TabsTrigger>
          <TabsTrigger value="exit" data-testid="tab-my-exit">Exit</TabsTrigger>
        </TabsList>

        <TabsContent value="attendance">
          {attendance.length === 0 ? (
            <EmptyState title="No attendance yet" description="Check in to start recording your attendance." testId="attendance-empty" />
          ) : (
            <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="my-attendance-table">
                <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                    <th className="px-5 py-3.5 font-semibold">Check-in</th>
                    <th className="px-5 py-3.5 font-semibold">Check-out</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((a) => (
                    <tr key={a.id} className="border-b border-border/40">
                      <td className="px-5 py-4 font-medium">{a.date}</td>
                      <td className="px-5 py-4 text-xs">{a.check_in ? new Date(a.check_in).toLocaleTimeString("en-IN") : "—"}</td>
                      <td className="px-5 py-4 text-xs">{a.check_out ? new Date(a.check_out).toLocaleTimeString("en-IN") : "—"}</td>
                      <td className="px-5 py-4"><StatusBadge status={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="leave">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white border border-border/60 rounded-xl p-6 space-y-4">
              <h3 className="font-heading font-semibold text-[#0B1B3D]">Apply for leave</h3>
              <div>
                <Label>Type</Label>
                <Select value={leave.type} onValueChange={(v) => setLeave({ ...leave, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="leave-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["casual", "sick", "earned", "maternity_paternity", "comp_off", "unpaid"].map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>From</Label><Input type="date" value={leave.from_date} onChange={(e) => setLeave({ ...leave, from_date: e.target.value })} className="mt-1.5" data-testid="leave-from" /></div>
                <div><Label>To</Label><Input type="date" value={leave.to_date} onChange={(e) => setLeave({ ...leave, to_date: e.target.value })} className="mt-1.5" data-testid="leave-to" /></div>
              </div>
              <div><Label>Reason</Label><Textarea rows={2} value={leave.reason} onChange={(e) => setLeave({ ...leave, reason: e.target.value })} className="mt-1.5" data-testid="leave-reason" /></div>
              <Button onClick={applyLeave} disabled={busy || !leave.from_date || !leave.to_date} className="w-full rounded-full bg-[#0B1B3D]" data-testid="apply-leave-btn">
                {busy ? "Submitting…" : "Submit for Approval"}
              </Button>
            </div>
            <div className="lg:col-span-2 space-y-3">
              {leaves.length === 0 ? (
                <EmptyState title="No leave requests" description="Your leave history will appear here." testId="my-leaves-empty" />
              ) : (
                leaves.map((l) => (
                  <div key={l.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between" data-testid={`my-leave-${l.id}`}>
                    <div>
                      <p className="font-semibold text-sm capitalize">{l.type.replace(/_/g, " ")}</p>
                      <p className="text-xs text-muted-foreground mt-1">{l.from_date} → {l.to_date}</p>
                    </div>
                    <StatusBadge status={l.status} />
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payslips">
          {payslips.length === 0 ? (
            <EmptyState title="No payslips yet" description="Payslips appear after your employer runs payroll." testId="payslips-empty" />
          ) : (
            <div className="space-y-3">
              {payslips.map((p) => (
                <div key={p.id} className="bg-white border border-border/60 rounded-xl p-5" data-testid={`payslip-${p.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-heading font-semibold text-[#0B1B3D]">{p.month}</p>
                    <p className="font-heading font-bold text-emerald-700">Net {inr(p.net_pay)}</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
                    <div><p className="text-muted-foreground">Basic</p><p className="font-semibold mt-0.5">{inr(p.earnings.basic)}</p></div>
                    <div><p className="text-muted-foreground">HRA</p><p className="font-semibold mt-0.5">{inr(p.earnings.hra)}</p></div>
                    <div><p className="text-muted-foreground">PF + PT + TDS</p><p className="font-semibold mt-0.5 text-red-600">-{inr(p.total_deductions)}</p></div>
                    <div><p className="text-muted-foreground">Gross</p><p className="font-semibold mt-0.5">{inr(p.gross)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="onboarding">
          {!onboarding ? (
            <EmptyState title="No onboarding checklist" description="Your onboarding checklist appears when you join a company." testId="onboarding-empty" />
          ) : (
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="onboarding-checklist">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-[#0B1B3D]">Onboarding checklist</h3>
                <span className="text-sm font-bold text-[#2563EB]" data-testid="onboarding-progress">Onboarding {onboarding.progress}% Complete</span>
              </div>
              <Progress value={onboarding.progress} className="h-2 mb-6" />
              <div className="grid sm:grid-cols-2 gap-3">
                {onboarding.items.map((i) => (
                  <div key={i.key} className={`border rounded-lg p-4 flex items-center justify-between ${i.status === "completed" ? "border-emerald-200 bg-emerald-50/50" : "border-border/60"}`} data-testid={`ob-item-${i.key}`}>
                    <p className="text-sm font-medium">{i.label}</p>
                    {i.status === "completed" ? (
                      <StatusBadge status="completed" />
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setItemFor(i); setItemData(""); }} data-testid={`ob-complete-${i.key}`}>
                        Complete
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="claims">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white border border-border/60 rounded-xl p-6 space-y-4">
              <h3 className="font-heading font-semibold text-[#0B1B3D]">New expense claim</h3>
              <div>
                <Label>Category</Label>
                <Select value={claim.category} onValueChange={(v) => setClaim({ ...claim, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="claim-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["travel", "food", "fuel", "mobile", "accommodation", "client_expenses", "other"].map((c) => (
                      <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (₹)</Label><Input type="number" value={claim.amount} onChange={(e) => setClaim({ ...claim, amount: e.target.value })} className="mt-1.5" data-testid="claim-amount" /></div>
                <div><Label>Date</Label><Input type="date" value={claim.date} onChange={(e) => setClaim({ ...claim, date: e.target.value })} className="mt-1.5" data-testid="claim-date" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={claim.description} onChange={(e) => setClaim({ ...claim, description: e.target.value })} className="mt-1.5" data-testid="claim-description" /></div>
              <Button onClick={submitClaim} disabled={busy || !claim.amount || !claim.date} className="w-full rounded-full bg-[#0B1B3D]" data-testid="claim-submit-btn">
                {busy ? "Submitting…" : "Submit Claim"}
              </Button>
            </div>
            <div className="lg:col-span-2 space-y-3">
              {claims.length === 0 ? (
                <EmptyState title="No claims yet" description="Your expense claims and their approval status will appear here." testId="my-claims-empty" />
              ) : (
                claims.map((c) => (
                  <div key={c.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between" data-testid={`my-claim-${c.id}`}>
                    <div>
                      <p className="font-semibold text-sm capitalize">{c.category.replace(/_/g, " ")} — {inr(c.amount)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{c.date} · {c.description}</p>
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          {docs.length === 0 ? (
            <EmptyState title="No documents yet" description="Offer letters, appointment letters and other documents shared by your employer appear here." testId="my-docs-empty" />
          ) : (
            <div className="space-y-3" data-testid="my-docs-list">
              {docs.map((d) => (
                <div key={d.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between" data-testid={`my-doc-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#2563EB]/10"><FileText className="w-4 h-4 text-[#2563EB]" /></div>
                    <div>
                      <p className="font-semibold text-sm">{d.template_name}</p>
                      <p className="text-xs text-muted-foreground">{d.company_name} · {fmtDate(d.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.signed_at && <StatusBadge status="signed" />}
                    <Button size="sm" variant="outline" className="rounded-full" onClick={async () => {
                      try {
                        const { data } = await api.get(`/documents/${d.id}`);
                        setViewDoc(data);
                      } catch (e) { toast.error(errMsg(e)); }
                    }} data-testid={`view-my-doc-${d.id}`}>View</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadPdf(d)} data-testid={`pdf-my-doc-${d.id}`}>PDF</Button>
                    {!d.signed_at && (
                      <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => { setSignFor(d); setSignName(""); }} data-testid={`sign-doc-${d.id}`}>Sign</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="performance">
          {goals.length === 0 ? (
            <EmptyState title="No goals assigned" description="Goals and KPIs assigned by your manager will appear here." testId="my-goals-empty" />
          ) : (
            <div className="space-y-3" data-testid="my-goals-list">
              {goals.map((g) => (
                <div key={g.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`my-goal-${g.id}`}>
                  <div>
                    <p className="font-semibold text-sm text-[#0B1B3D]">{g.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      KPI: {g.kpi || "—"} · Due {g.due_date || "flexible"}
                      {g.self_review && ` · My self-assessment: ${g.self_review.rating}/5`}
                      {g.manager_review && ` · Manager rating: ${g.manager_review.rating}/5 — ${g.manager_review.feedback || ""}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold rounded-full px-3 py-1 ${
                      g.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                      g.status === "self_reviewed" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {g.status === "completed" ? "Reviewed" : g.status === "self_reviewed" ? "Self-reviewed" : "Assigned"}
                    </span>
                    {g.status === "assigned" && (
                      <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => { setAssessFor(g); setAssess({ rating: 4, notes: "" }); }} data-testid={`self-assess-${g.id}`}>
                        Self-Assess
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="assets">
          {assets.length === 0 ? (
            <EmptyState title="No assets" description="Assets issued by your employer will appear here for acknowledgement." testId="my-assets-empty" />
          ) : (
            <div className="space-y-3" data-testid="my-assets-list">
              {assets.map((a) => (
                <div key={a.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between" data-testid={`my-asset-${a.id}`}>
                  <div>
                    <p className="font-semibold text-sm">{a.name} <span className="text-xs text-muted-foreground capitalize">({a.type.replace(/_/g, " ")})</span></p>
                    <p className="text-xs text-muted-foreground mt-1">{a.serial && `S/N ${a.serial} · `}Issued {fmtDate(a.created_at)}</p>
                  </div>
                  {a.status === "issued" ? (
                    <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => ackAsset(a.id)} data-testid={`ack-asset-${a.id}`}>Acknowledge Receipt</Button>
                  ) : (
                    <StatusBadge status={a.status} />
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="my-holidays">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Company holidays</h3>
              {calendar.holidays.length === 0 ? (
                <p className="text-sm text-muted-foreground">No holidays published yet.</p>
              ) : (
                <div className="space-y-2">
                  {calendar.holidays.map((h) => (
                    <div key={h.id} className="flex items-center justify-between border border-border/60 rounded-lg px-4 py-2.5" data-testid={`my-holiday-${h.id}`}>
                      <p className="text-sm font-medium">{h.name}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(h.date)} · <span className="capitalize">{h.type}</span></p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="my-shift">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">My shift</h3>
              {calendar.shift ? (
                <div>
                  <p className="font-semibold">{calendar.shift.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {calendar.shift.start_time} – {calendar.shift.end_time} · {calendar.shift.days.join(", ")}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No shift assigned yet.</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="exit">
          {!exitInfo || exitInfo.status === "withdrawn" ? (
            <div className="bg-white border border-border/60 rounded-xl p-6 max-w-lg" data-testid="exit-start-card">
              <h3 className="font-heading font-semibold text-[#0B1B3D]">Resignation</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Submitting a resignation starts the exit workflow: approval → notice period → asset clearance → full & final settlement → relieving letter.
              </p>
              <Button onClick={() => setResignModal(true)} variant="outline" className="rounded-full mt-5 text-red-600 border-red-200" data-testid="resign-btn">
                Submit Resignation
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-border/60 rounded-xl p-6 max-w-2xl" data-testid="exit-status-card">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-semibold text-[#0B1B3D]">Exit process</h3>
                <StatusBadge status={exitInfo.status} />
              </div>
              <div className="flex gap-0 flex-wrap">
                {["resigned", "notice_period", "clearance", "fnf_processed", "exited"].map((s, i) => {
                  const stages = ["resigned", "notice_period", "clearance", "fnf_processed", "exited"];
                  const current = stages.indexOf(exitInfo.status);
                  const done = i <= current;
                  return (
                    <div key={s} className="flex items-center">
                      <div className={`text-[11px] font-semibold rounded-full px-3 py-1.5 capitalize ${done ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-500"}`}>
                        {s.replace(/_/g, " ")}
                      </div>
                      {i < 4 && <div className={`w-6 h-0.5 ${i < current ? "bg-[#2563EB]" : "bg-border"}`} />}
                    </div>
                  );
                })}
              </div>
              {exitInfo.fnf_amount > 0 && (
                <p className="text-sm mt-4">Full & final settlement: <span className="font-bold">{inr(exitInfo.fnf_amount)}</span></p>
              )}
              <div className="mt-4 space-y-1">
                {exitInfo.history?.map((h, i) => (
                  <p key={i} className="text-xs text-muted-foreground capitalize">
                    {h.action.replace(/_/g, " ")} — {h.by} · {fmtDate(h.at)}
                  </p>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={resignModal} onOpenChange={setResignModal}>
        <DialogContent aria-describedby={undefined} data-testid="resign-dialog">
          <DialogHeader><DialogTitle>Submit resignation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Reason</Label><Textarea rows={3} value={resign.reason} onChange={(e) => setResign({ ...resign, reason: e.target.value })} className="mt-1.5" data-testid="resign-reason" /></div>
            <div><Label>Proposed last working day</Label><Input type="date" value={resign.proposed_last_date} onChange={(e) => setResign({ ...resign, proposed_last_date: e.target.value })} className="mt-1.5" data-testid="resign-date" /></div>
            <Button onClick={submitResign} disabled={busy} className="w-full rounded-full bg-red-600 hover:bg-red-700" data-testid="resign-submit-btn">
              {busy ? "Submitting…" : "Submit Resignation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signFor} onOpenChange={() => setSignFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="sign-dialog">
          <DialogHeader><DialogTitle>Sign — {signFor?.template_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Type your full legal name to electronically sign this document.</p>
          <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Your full name" data-testid="sign-name-input" />
          <Button onClick={signDoc} disabled={!signName.trim()} className="w-full rounded-full bg-[#0B1B3D]" data-testid="sign-submit-btn">Sign Document</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!itemFor} onOpenChange={() => setItemFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="ob-item-dialog">
          <DialogHeader><DialogTitle>{itemFor?.label}</DialogTitle></DialogHeader>
          <div>
            <Label>Details / reference</Label>
            <Textarea rows={3} value={itemData} onChange={(e) => setItemData(e.target.value)} placeholder="Enter details or a reference note" className="mt-1.5" data-testid="ob-item-data" />
          </div>
          <Button onClick={completeItem} className="w-full rounded-full bg-[#0B1B3D]" data-testid="ob-item-submit">Mark Complete</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="my-doc-dialog">
          <DialogHeader><DialogTitle>{viewDoc?.template_name}</DialogTitle></DialogHeader>
          <div className="bg-white border border-border rounded-lg p-6 whitespace-pre-line text-sm leading-relaxed font-serif max-h-[60vh] overflow-y-auto">
            {viewDoc?.body}
          </div>
          {viewDoc?.signed_at && (
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Digitally signed by {viewDoc.signed_by} on {fmtDate(viewDoc.signed_at)}
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="my-doc-print">Print</Button>
            <Button variant="outline" className="rounded-full" onClick={() => downloadPdf(viewDoc)} data-testid="my-doc-pdf">Download PDF</Button>
            {!viewDoc?.signed_at && (
              <Button className="rounded-full bg-[#0B1B3D]" onClick={() => { setSignFor(viewDoc); setSignName(""); }} data-testid="my-doc-sign">Sign</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assessFor} onOpenChange={() => setAssessFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="assess-dialog">
          <DialogHeader><DialogTitle>Self-assessment — {assessFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rating (1–5)</Label>
              <Input type="number" min={1} max={5} value={assess.rating} onChange={(e) => setAssess({ ...assess, rating: e.target.value })} className="mt-1.5" data-testid="assess-rating" />
            </div>
            <div><Label>Notes</Label><Textarea rows={3} value={assess.notes} onChange={(e) => setAssess({ ...assess, notes: e.target.value })} className="mt-1.5" data-testid="assess-notes" /></div>
            <Button onClick={submitAssess} className="w-full rounded-full bg-[#0B1B3D]" data-testid="assess-submit">Submit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
